import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import CallToAction from './components/CallToAction';
import { lazy, Suspense } from 'react';

import Home from './pages/Home';
import Blog from './pages/Blog';
import Contact from './pages/Contact';
import About from './pages/About';
import Services from './pages/Services';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import BlogPostDetail from './pages/BlogPostDetail';
import Faq from './pages/Faq';
import Demos from './pages/Demos';
import DemoViewer from './pages/DemoViewer';
import NotFound from './pages/NotFound';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse';

import ScrollToTopButton from './components/ScrollToTop';

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <div className="w-12 h-12 border-4 border-brand-blue/20 border-t-brand-blue rounded-full animate-spin"></div>
  </div>
);

import { useTranslation } from 'react-i18next';
import { useDataStore } from './store/dataStore';
import Maintenance from './pages/Maintenance';

// Layout component to handle conditional rendering of CTA
const Layout = () => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const { siteSettings } = useDataStore();
  
  // Don't show the main CTA on the Contact page itself
  const showCTA = location.pathname !== '/iletisim' && location.pathname !== '/en/contact' && location.pathname !== '/demo-incele';
  
  // Don't show Navbar/Footer on DemoViewer
  const isDemoViewer = location.pathname === '/demo-incele';

  if (siteSettings?.maintenanceMode) {
    return <Maintenance />;
  }

  return (
    <div className="flex flex-col min-h-screen font-sans antialiased text-gray-800 bg-brand-light selection:bg-brand-blue selection:text-white">
      {!isDemoViewer && <Navbar />}
      <main className="flex-grow">
            <Routes location={location} key={location.pathname}>
              {i18n.language === 'en' ? (
                <>
                  <Route path="/" element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/projects" element={<Projects />} />
                  <Route path="/projelerimiz/:slug" element={<ProjectDetail />} />
                  {/* <Route path="/demolar" element={<Demos />} /> */}
                  {/* <Route path="/demo-incele" element={<DemoViewer />} /> */}
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogPostDetail />} />
                  <Route path="/faq" element={<Faq />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-use" element={<TermsOfUse />} />
                  <Route path="*" element={<NotFound />} />
                </>
              ) : (
                <>
                  <Route path="/" element={<Home />} />
                  <Route path="/hakkimizda" element={<About />} />
                  <Route path="/hizmetlerimiz" element={<Services />} />
                  <Route path="/projelerimiz" element={<Projects />} />
                  <Route path="/projelerimiz/:slug" element={<ProjectDetail />} />
                  {/* <Route path="/demolar" element={<Demos />} /> */}
                  {/* <Route path="/demo-incele" element={<DemoViewer />} /> */}
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogPostDetail />} />
                  <Route path="/sss" element={<Faq />} />
                  <Route path="/iletisim" element={<Contact />} />
                  <Route path="/gizlilik-politikasi" element={<PrivacyPolicy />} />
                  <Route path="/kullanim-sartlari" element={<TermsOfUse />} />
                  <Route path="*" element={<NotFound />} />
                </>
              )}
            </Routes>
      </main>
      {showCTA && <CallToAction />}
      {!isDemoViewer && <Footer />}
    </div>
  );
};

import { HelmetProvider } from 'react-helmet-async';
import { DataProvider } from './context/DataContext';
import { LanguageWrapper } from './components/LanguageWrapper';

// Lazy loaded admin components
const AdminLogin = lazy(() => import('./pages/admin/Login'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminPrices = lazy(() => import('./pages/admin/Prices'));
const AdminProjects = lazy(() => import('./pages/admin/Projects'));
const AdminBlog = lazy(() => import('./pages/admin/Blog'));
const AdminTeam = lazy(() => import('./pages/admin/Team'));
const AdminLegal = lazy(() => import('./pages/admin/Legal'));
const AdminContactSettings = lazy(() => import('./pages/admin/ContactSettings'));
const AdminBrands = lazy(() => import('./pages/admin/Brands'));
const AdminFaq = lazy(() => import('./pages/admin/FaqAdmin'));
const AdminProfile = lazy(() => import('./pages/admin/Profile'));
const AdminSettings = lazy(() => import('./pages/admin/Settings'));
const AdminSeoSettings = lazy(() => import('./pages/admin/SeoSettings'));

import ProtectedRoute from './components/admin/ProtectedRoute';

const App: React.FC = () => {
  return (
    <DataProvider>
      <HelmetProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            {/* Admin Routes with Suspense */}
            <Route path="/admin/login" element={
              <Suspense fallback={<PageLoader />}>
                <AdminLogin />
              </Suspense>
            } />
            
            {/* Protected Admin Routes */}
            <Route path="/admin" element={<ProtectedRoute />}>
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={
                <Suspense fallback={<PageLoader />}><AdminDashboard /></Suspense>
              } />
              <Route path="prices" element={
                <Suspense fallback={<PageLoader />}><AdminPrices /></Suspense>
              } />
              <Route path="projects" element={
                <Suspense fallback={<PageLoader />}><AdminProjects /></Suspense>
              } />
              <Route path="blog" element={
                <Suspense fallback={<PageLoader />}><AdminBlog /></Suspense>
              } />
              <Route path="team" element={
                <Suspense fallback={<PageLoader />}><AdminTeam /></Suspense>
              } />
              <Route path="legal" element={
                <Suspense fallback={<PageLoader />}><AdminLegal /></Suspense>
              } />
              <Route path="contact-settings" element={
                <Suspense fallback={<PageLoader />}><AdminContactSettings /></Suspense>
              } />
              <Route path="brands" element={
                <Suspense fallback={<PageLoader />}><AdminBrands /></Suspense>
              } />
              <Route path="faq" element={
                <Suspense fallback={<PageLoader />}><AdminFaq /></Suspense>
              } />
              <Route path="profile" element={
                <Suspense fallback={<PageLoader />}><AdminProfile /></Suspense>
              } />
              <Route path="settings" element={
                <Suspense fallback={<PageLoader />}><AdminSettings /></Suspense>
              } />
              <Route path="seo" element={
                <Suspense fallback={<PageLoader />}><AdminSeoSettings /></Suspense>
              } />
            </Route>

            {/* English routes disabled for now */}
            <Route path="/en/*" element={<Navigate to="/" replace />} />

            {/* Turkish routes (Default) */}
            <Route path="/*" element={
              <LanguageWrapper lang="tr" />
            }>
              <Route path="*" element={<Layout />} />
            </Route>
          </Routes>
          <ScrollToTopButton />
        </BrowserRouter>
      </HelmetProvider>
    </DataProvider>
  );
};

export default App;